import { ArrowTopRightOnSquareIcon, TruckIcon } from "@heroicons/react/24/outline";

import { IPart } from "../../../models/Part";
import ClickToCopy from "../../shared/ClickToCopy";
import openInNewTab from "../../../utils/openInNewTab";
import UnlistedKTypeUpdate from "./UnlistedKTypeUpdate";
import UnlistedPaintCodeUpdate from "./UnlistedPaintCodeUpdate";

type Props = { part: IPart };

type SpecChip = { label: string; value: string; tone: string };

const SPEC_TONE = {
  slate: "bg-slate-100 text-slate-700 ring-slate-200",
  indigo: "bg-indigo-50 text-indigo-800 ring-indigo-200/80",
  amber: "bg-amber-50 text-amber-900 ring-amber-200/80",
  emerald: "bg-emerald-50 text-emerald-900 ring-emerald-200/80",
} as const;

export default function UnlistedDonorVehicleCell({ part }: Props) {
  const v = part.vehicle;
  const miles = v?.odometerInMiles
    ? `${v.odometerInMiles.toLocaleString()} miles`
    : "No mileage found";
  const year = v?.dvlaYearOfManufacture;
  const hasVehicle = Boolean(v?.id);

  const bodyStyle = v?.bodyStyle || v?.bodyStyleDescription || null;
  const doors =
    typeof v?.numberOfDoors === "number" && v.numberOfDoors > 0
      ? `${v.numberOfDoors}dr`
      : null;
  const fuel = v?.combinedFuelType || v?.fuelType || v?.fuelDescription || null;
  const engineCode = v?.engineModelCode || null;

  const specs: SpecChip[] = [
    bodyStyle && { label: "Body", value: bodyStyle, tone: SPEC_TONE.slate },
    doors && { label: "Doors", value: doors, tone: SPEC_TONE.indigo },
    fuel && { label: "Fuel", value: fuel, tone: SPEC_TONE.emerald },
    engineCode && {
      label: "Engine code",
      value: engineCode,
      tone: SPEC_TONE.amber,
    },
  ].filter(Boolean) as SpecChip[];

  return (
    <div
      className="flex h-full min-w-0 flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex h-full flex-col rounded-xl border border-slate-200/90 bg-gradient-to-b from-slate-50/95 to-white shadow-sm ring-1 ring-slate-200/50">
        <div className="flex items-center justify-between gap-1.5 border-b border-slate-200/80 px-2 py-1">
          <div className="flex min-w-0 items-center gap-1 text-slate-600">
            <TruckIcon className="h-3 w-3 shrink-0" aria-hidden />
            <p className="truncate text-[10px] font-semibold uppercase tracking-wide">
              Donor vehicle
            </p>
          </div>
          {hasVehicle ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openInNewTab(`/vehicles/${v!.id}`);
              }}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
              aria-label="Open donor vehicle in a new tab"
              title="Open donor vehicle in a new tab"
            >
              View
              <ArrowTopRightOnSquareIcon className="h-3 w-3" aria-hidden />
            </button>
          ) : null}
        </div>

        <div className="px-2 py-2">
          <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
            {v?.vrm ? (
              <span className="inline-flex items-center rounded-md bg-slate-900 px-1.5 py-0.5 font-mono text-sm font-semibold uppercase tracking-wider text-amber-300 shadow-sm ring-1 ring-inset ring-slate-800">
                <ClickToCopy text={v.vrm} textClassName="text-amber-300" />
              </span>
            ) : (
              <span className="text-slate-400">—</span>
            )}
            {year ? (
              <span className="text-xs font-medium text-slate-500">{year}</span>
            ) : null}
          </div>

          <div className="mt-1.5 flex min-w-0 flex-col text-sm leading-snug text-slate-800">
            <span
              className="truncate font-medium"
              title={v?.combinedMake ?? ""}
            >
              {v?.combinedMake || <span className="text-slate-400">—</span>}
            </span>
            <span className="truncate text-slate-600" title={v?.combinedModel ?? ""}>
              {v?.combinedModel || ""}
            </span>
          </div>

          <dl className="mt-1.5 grid grid-cols-[auto,1fr] gap-x-1.5 gap-y-0.5 text-xs">
            <dt className="text-slate-500">VRM</dt>
            <dd className="min-w-0 text-slate-800">
              {v?.vrm ? (
                <ClickToCopy
                  text={v.vrm}
                  textClassName="text-xs text-slate-700"
                />
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </dd>
            <dt className="text-slate-500">VIN</dt>
            <dd className="min-w-0 text-slate-800">
              {v?.combinedVIN ? (
                <ClickToCopy
                  text={v.combinedVIN}
                  textClassName="text-xs text-slate-700"
                />
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </dd>
            <dt className="text-slate-500">Mileage</dt>
            <dd className="text-slate-800">{miles}</dd>
          </dl>

          {specs.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {specs.map((s) => (
                <span
                  key={s.label}
                  title={`${s.label}: ${s.value}`}
                  className={`inline-flex max-w-full items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${s.tone}`}
                >
                  <span className="text-[9px] font-semibold uppercase tracking-wide opacity-70">
                    {s.label}
                  </span>
                  <span className="truncate">{s.value}</span>
                </span>
              ))}
            </div>
          )}

          {hasVehicle && (
            <div className="mt-2 space-y-1 border-t border-slate-200/80 pt-2">
              <UnlistedKTypeUpdate part={part} />
              <UnlistedPaintCodeUpdate part={part} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
